import { Button, Grid, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import PortSelector from '../PortSelector/PortSelector';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import PortModel from '../PortSelector/models/PortModel';
import { resetTonnageInput, setDateOpenEnd, setDateOpenStart, setPortOpen, setSelectedTonnageId, setTriggerFocusTonnageNameQuickInput, setTonnageInput, setVesselName, setTriggerSave, } from './reducers/TonnageQuickInputReducer';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import Utility from '../../app/utility';
import BaseResponse from '../../app/BaseResponse';
import { tonnageQuickInputSelector } from './reducers/TonnageQuickInputReducer';
import TonnageApi, { SaveTonnageParam } from '../Tonnage/api/TonnageApi';
import TonnageModel from '../Tonnage/models/TonnageModel';
import { TonnageInputModel, addTonnageName } from '../Tonnage/reducers/TonnageInputReducer';
import TonnageNameModel from '../Tonnage/models/TonnageNameModel';
import TonnageNameSelector from '../Tonnage/components/TonnageNameSelector';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import moment from 'moment';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';

const TonnageQuickInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const tonnageInputViewData = useSelector(tonnageQuickInputSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);

    useEffect(() => {
        if (tonnageInputViewData.triggerSave > 0) {
            saveTonnage();
            dispatch(setTriggerSave(0));
        }
    }, [tonnageInputViewData.triggerSave]);

    useEffect(() => {
        if (tonnageInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [tonnageInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetTonnageInput());
        dispatch(setSelectedTonnageId(""));
        dispatch(setTriggerFocusTonnageNameQuickInput((new Date()).getTime()));
    }

    const saveTonnage = () => {
        try {
            validateTonnage();

            let isUpdateFullTc = tonnageInputViewData.tonnageInput.id.length === 0 ?
                true :
                tonnageInputViewData.tonnageInput.updateFullTc;

            let param = {
                id: tonnageInputViewData.tonnageInput.id,
                vesselName: tonnageInputViewData.tonnageInput.vesselName,
                portOpen: tonnageInputViewData.tonnageInput.portOpen,
                dateOpenStart: tonnageInputViewData.tonnageInput.dateOpenStart,
                dateOpenEnd: tonnageInputViewData.tonnageInput.dateOpenEnd,
                dwt: tonnageInputViewData.tonnageInput.dwt,
                yearBuilt: tonnageInputViewData.tonnageInput.yearBuilt,
                class: tonnageInputViewData.tonnageInput.class,
                deck: tonnageInputViewData.tonnageInput.deck,
                loa: tonnageInputViewData.tonnageInput.loa,
                beam: tonnageInputViewData.tonnageInput.beam,
                draft: tonnageInputViewData.tonnageInput.draft,
                gear: tonnageInputViewData.tonnageInput.gear,
                grabs: tonnageInputViewData.tonnageInput.grabs,
                operator: tonnageInputViewData.tonnageInput.operator,
                headOwner: tonnageInputViewData.tonnageInput.headOwner,
                remarks: tonnageInputViewData.tonnageInput.remarks,
                capacity: tonnageInputViewData.tonnageInput.capacity,
                fullTC: isUpdateFullTc ? tonnageInputViewData.tonnageInput.fullTc : "",
                updateFullTc: isUpdateFullTc,
            } as SaveTonnageParam;

            TonnageApi.Save(param)
                .then((response: BaseResponse<TonnageModel>) => {
                    if (response.success) {
                        if (param.id.length === 0) {
                            Utility.Alert.Success("Tonnage added successful");

                            dispatch(addTonnageName({
                                id: response.data.id,
                                dwt: response.data.dwt,
                                vesselName: response.data.vesselName
                            } as TonnageNameModel));
                        } else {
                            Utility.Alert.Success("Tonnage updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });

        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validateTonnage = () => {
        if (tonnageInputViewData.tonnageInput.vesselName.length === 0) {
            throw "Vessel Name is required";
        }

        if (tonnageInputViewData.tonnageInput.portOpen === 0) {
            throw "Port open is required";
        }

        if (tonnageInputViewData.tonnageInput.dateOpenStart.length === 0 ||
            !Utility.DateFormat.isValidDate(tonnageInputViewData.tonnageInput.dateOpenStart)) {
            throw "Port open start date is required";
        }

        if (tonnageInputViewData.tonnageInput.dateOpenEnd.length === 0 ||
            !Utility.DateFormat.isValidDate(tonnageInputViewData.tonnageInput.dateOpenEnd)) {
            throw "Port open end date is required";
        }
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    useEffect(() => {
        if (tonnageInputViewData.selectedTonnageId.length > 0) {
            selectTonnageById(tonnageInputViewData.selectedTonnageId);
        }
    }, [tonnageInputViewData.selectedTonnageId])

    const selectTonnageById = (id: string) => {
        dispatch(showLoading(true));
        TonnageApi.GetById({
            id: id
        }).then((response: BaseResponse<TonnageModel>) => {
            if (response.success) {
                let tonnageInput = Utility.CloneObject(tonnageInputViewData.tonnageInput);

                tonnageInput.id = response.data.id;
                tonnageInput.headOwner = response.data.headOwner ? response.data.headOwner : "";
                tonnageInput.operator = response.data.operator ? response.data.operator : "";
                tonnageInput.vesselName = response.data.vesselName;
                tonnageInput.dwt = response.data.dwt ? response.data.dwt : null;
                tonnageInput.yearBuilt = response.data.yearBuilt ? response.data.yearBuilt : null;
                tonnageInput.portOpen = response.data.portOpen;
                tonnageInput.dateOpenStart = Utility.DateFormat.format(new Date(response.data.dateOpenStart));
                tonnageInput.dateOpenEnd = Utility.DateFormat.format(new Date(response.data.dateOpenEnd));
                tonnageInput.remarks = response.data.remarks ? response.data.remarks : "";
                tonnageInput.gear = response.data.gear ? response.data.gear : null;
                tonnageInput.draft = response.data.draft ? response.data.draft : null;
                tonnageInput.loa = response.data.loa ? response.data.loa : null;
                tonnageInput.class = response.data.class ? response.data.class : "";
                tonnageInput.beam = response.data.beam ? response.data.beam : null;
                tonnageInput.grabs = response.data.grabs;
                tonnageInput.deck = response.data.deck ? response.data.deck : "";
                tonnageInput.capacity = response.data.capacity ? response.data.capacity : null;
                tonnageInput.updateFullTc = false;
                tonnageInput.fullTc = response.data.fullTcpreview;

                Utility.Alert.Info("Last updated by " + response.data.userDisplayName + " " + formatUpdatedDate(response.data.updatedDate));

                setContactForContactSelector(response.data);

                dispatch(setTonnageInput(tonnageInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const setContactForContactSelector = (data: TonnageModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        searchResults.push({
            id: data.operator,
            companyName: data.operatorName
        } as ContactNameModel);
        searchResults.push({
            id: data.headOwner,
            companyName: data.headOwnerName
        } as ContactNameModel);

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12} className="quick-input-component">
                    <Grid container>
                        <Grid item lg={9} xs={10}>
                            <TonnageNameSelector
                                triggerFocusInput={tonnageInputViewData.triggerFocusTonnageNameQuickInput}
                                selectedTonnageId={tonnageInputViewData.selectedTonnageId}
                                currentVesselName={tonnageInputViewData.tonnageInput.vesselName}
                                onTonnageIdChange={(value: string) => {
                                    dispatch(setSelectedTonnageId(value));
                                }}
                                onVesselNameChange={(value: string) => {
                                    dispatch(setVesselName(value));
                                }}
                            >
                            </TonnageNameSelector>
                        </Grid>
                        <Grid item lg={3} xs={2}>
                            <Tooltip title="Reset">
                                <Button
                                    className="clear-input-button"
                                    color="success"
                                    variant="contained"
                                    onClick={() => {
                                        refreshInput();
                                    }}
                                >
                                    <i className="fa fa-refresh" aria-hidden="true"></i>
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} className="quick-input-component">
                    <PortSelector
                        onPortChange={(value: PortModel | null) => {
                            if (value) {
                                dispatch(setPortOpen((value as PortModel).id));
                            } else {
                                dispatch(setPortOpen(0));
                            }
                        }}
                        portLabel='Port Open'
                        areaLabel='Area'
                        isAreaDisabled={true}
                        portValue={getPortById(tonnageInputViewData.tonnageInput.portOpen)}
                        isAreaRelatedToPort={true}
                    >
                    </PortSelector>
                </Grid>
                <Grid item xs={12} className="quick-input-component">
                    <StartEndDatepicker
                        startDateLabel="Port open start date"
                        endDateLabel="Port open end date"
                        useQuickEndDateInput={true}
                        onStartDateChange={(date: string) => {
                            if (date) {
                                dispatch(setDateOpenStart(date));
                            }
                        }}
                        onEndDateChange={(date: string) => {
                            if (date) {
                                dispatch(setDateOpenEnd(date));
                            }
                        }}
                        startDateValue={tonnageInputViewData.tonnageInput.dateOpenStart.toUpperCase()}
                        endDateValue={tonnageInputViewData.tonnageInput.dateOpenEnd.toUpperCase()}
                    >
                    </StartEndDatepicker>
                </Grid>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item lg={12}>
                            <Button
                                onClick={saveTonnage}
                                variant="outlined"
                            >
                                Save (s)
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
};

export default TonnageQuickInput;